import React, { useEffect, useState, useCallback } from 'react';
import { Users, Plus, Upload, Trash2, Download, ListFilter, UserPlus, Inbox } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Panel, EmptyState, Card, Field, Chip, Stat, inputCls } from './shared';
import { toast } from 'sonner';

export default function AudiencePanel() {
  const [tab, setTab] = useState('contacts');
  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [leads, setLeads] = useState([]);
  const [activeList, setActiveList] = useState('');
  const [q, setQ] = useState('');
  const [newList, setNewList] = useState('');
  const [contact, setContact] = useState({ email: '', name: '', phone: '', company: '' });

  const load = useCallback(() => {
    suiteAPI.getLists().then(r => setLists(r.data));
    suiteAPI.listContacts({ list_id: activeList || undefined, q: q || undefined })
      .then(r => { setContacts(r.data.contacts); setTotal(r.data.total); });
    suiteAPI.listLeads({}).then(r => setLeads(r.data));
  }, [activeList, q]);
  useEffect(() => { load(); }, [load]);

  const createList = async () => {
    if (!newList.trim()) return;
    await suiteAPI.createList({ name: newList });
    setNewList(''); load(); toast.success('List created');
  };

  const addContact = async () => {
    if (!contact.email && !contact.phone) return toast.error('Email or phone required');
    try {
      await suiteAPI.addContact({ ...contact, list_ids: activeList ? [activeList] : [] });
      setContact({ email: '', name: '', phone: '', company: '' });
      load(); toast.success('Contact added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add contact'); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data } = await suiteAPI.importContacts(fd, activeList || undefined);
      toast.success(`Imported ${data.added} contacts (${data.skipped} skipped)`);
      load();
    } catch { toast.error('Import failed'); }
  };

  return (
    <Panel title="Audience" subtitle="Contacts, segments and every lead captured across your landing pages and forms." testId="suite-audience"
      action={
        <div className="flex gap-2">
          <label>
            <Button asChild className="bg-zinc-800 text-white"><span className="cursor-pointer flex items-center"><Upload size={14} className="mr-1.5" /> Import CSV</span></Button>
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="contacts-import" />
          </label>
          <a href={suiteAPI.exportCsvUrl('contacts')} target="_blank" rel="noreferrer"><Button className="bg-zinc-800 text-white"><Download size={14} className="mr-1.5" /> Export</Button></a>
        </div>
      }>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Contacts" value={total} />
        <Stat label="Lists" value={lists.length} />
        <Stat label="Leads captured" value={leads.length} tone="text-[#b8ff00]" />
        <Stat label="New leads" value={leads.filter(l => l.status === 'new').length} tone="text-cyan-400" />
      </div>

      <div className="flex gap-1.5 mb-4">
        {[['contacts', 'Contacts'], ['lists', 'Lists'], ['leads', 'Leads']].map(([v, l]) => (
          <Chip key={v} active={tab === v} onClick={() => setTab(v)} testId={`audience-tab-${v}`}>{l}</Chip>
        ))}
      </div>

      {tab === 'contacts' && (
        <div className="grid lg:grid-cols-[340px_1fr] gap-5">
          <Card className="p-5 space-y-3 h-fit">
            <p className="text-sm font-bold text-white flex items-center gap-1.5"><UserPlus size={14} /> Add contact</p>
            <Field label="Email"><Input value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} className={inputCls} data-testid="contact-email" /></Field>
            <Field label="Name"><Input value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} className={inputCls} data-testid="contact-name" /></Field>
            <Field label="Phone (E.164 for SMS)" hint="+15551234567"><Input value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} className={inputCls} data-testid="contact-phone" /></Field>
            <Field label="Company"><Input value={contact.company} onChange={e => setContact({ ...contact, company: e.target.value })} className={inputCls} /></Field>
            <Field label="Add to list">
              <select value={activeList} onChange={e => setActiveList(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="contact-list">
                <option value="">No list</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Button onClick={addContact} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="contact-add"><Plus size={14} className="mr-1.5" /> Add contact</Button>
          </Card>
          <div>
            <div className="flex gap-2 mb-3">
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search contacts…" className={`${inputCls} max-w-xs`} data-testid="contacts-search" />
              <select value={activeList} onChange={e => setActiveList(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3">
                <option value="">All lists</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            {contacts.length === 0 ? <EmptyState icon={Users} text="No contacts yet. Add one or import a CSV (email,name,phone,company,tags)." /> : (
              <Card className="divide-y divide-zinc-800">
                {contacts.map(c => (
                  <div key={c.id} className="p-3 flex items-center justify-between gap-3" data-testid={`contact-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{c.name || c.email}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{c.email} {c.phone ? `· ${c.phone}` : ''} · {c.source}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!c.subscribed && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">unsubscribed</span>}
                      <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteContact(c.id).then(load)}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                ))}
              </Card>
            )}
          </div>
        </div>
      )}

      {tab === 'lists' && (
        <div className="space-y-3">
          <div className="flex gap-2 max-w-md">
            <Input value={newList} onChange={e => setNewList(e.target.value)} placeholder="New list name" className={inputCls} data-testid="list-name" />
            <Button onClick={createList} className="bg-[#b8ff00] text-black font-bold" data-testid="list-create"><Plus size={14} /></Button>
          </div>
          {lists.length === 0 ? <EmptyState icon={ListFilter} text="No lists yet." /> : (
            <div className="grid md:grid-cols-3 gap-3">
              {lists.map(l => (
                <Card key={l.id} className="p-4 flex items-center justify-between" data-testid={`list-${l.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-white">{l.name}</p>
                    <p className="text-[11px] text-zinc-500">{l.contact_count} contacts</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteList(l.id).then(load)}><Trash2 size={13} /></Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'leads' && (
        leads.length === 0 ? <EmptyState icon={Inbox} text="No leads yet. Publish a landing page to start capturing." /> : (
          <Card className="divide-y divide-zinc-800">
            {leads.map(l => (
              <div key={l.id} className="p-3 flex items-center justify-between gap-3" data-testid={`lead-${l.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{l.name || l.email || 'Anonymous'}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{l.email} {l.phone ? `· ${l.phone}` : ''} · {l.source} · {l.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={l.status} onChange={e => suiteAPI.updateLead(l.id, { status: e.target.value }).then(load)} className="bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-300 px-2 py-1" data-testid={`lead-status-${l.id}`}>
                    {['new', 'contacted', 'qualified', 'won', 'lost'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteLead(l.id).then(load)}><Trash2 size={13} /></Button>
                </div>
              </div>
            ))}
          </Card>
        )
      )}
    </Panel>
  );
}
